import { useCallback } from 'react';
import type { IssueUpdatedHandler } from '@atlassian/jira-business-fields/src/common/types.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import type { ParentFieldOption } from '@atlassian/jira-issue-field-parent/src/common/types';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config';
import { useListViewUpdaterService } from '../list-view-updater';

const canRemoveIssue = ({
	fieldType,
	issueKey,
	newValue,
	oldValue,
}: {
	fieldType: string;
	issueKey?: string;
	newValue: ParentFieldOption | null;
	oldValue: ParentFieldOption | null;
}) => {
	switch (fieldType) {
		case PARENT_TYPE:
			if (ff('list-uses-new-jql-parent-project-directive_v5gfs')) {
				const currentProject = (issueKey ?? '').split('-')[0];
				const oldValueProjectKey = (oldValue?.key ?? '').split('-')[0];
				const newValueProjectKey = (newValue?.key ?? '').split('-')[0];

				if (
					// parent from foreign project is assigned
					(!oldValue?.value && newValueProjectKey !== currentProject) ||
					// parent from foreign project is unlinked
					(!newValue?.value && oldValueProjectKey !== currentProject)
				) {
					return false;
				}
			}

			return newValue?.value !== oldValue?.value;

		default:
			return false;
	}
};

export const useIssueUpdated = () => {
	const { onIssueRemoved } = useListViewUpdaterService();

	const onIssueUpdated = useCallback<IssueUpdatedHandler>(
		({ issueKey, issueId, fieldType, newValue, oldValue }) => {
			if (canRemoveIssue({ issueKey, fieldType, newValue, oldValue })) {
				onIssueRemoved({
					issueId: Number(issueId),
					parentId: oldValue?.value ?? undefined,
				});
			}
		},
		[onIssueRemoved],
	);

	return {
		onIssueUpdated,
	};
};
